import React, { useState } from 'react';
import { cashManagementData } from '../../data/financialManagementData';
import { financialTransactionsData } from '../../data/financialTransactionsData';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowUpRight, ArrowDownRight, DollarSign, Calendar, Activity, TrendingUp, Filter, Search, Download } from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const CashManagementDashboard = () => {
  const { cashAccounts, recentTransactions, scheduledPayments, cashFlowKPIs } = cashManagementData;
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Combine transactions from financial transactions data
  const allTransactions = [
    ...financialTransactionsData.salesOrderTransactions.map(t => ({
      transaction_id: t.transaction_id,
      description: t.description,
      amount: t.amount,
      currency: t.currency,
      transaction_date: t.transaction_date,
      reference: t.reference_id,
      status: t.status,
      type: t.transaction_type
    })),
    ...financialTransactionsData.purchaseOrderTransactions.map(t => ({
      transaction_id: t.transaction_id,
      description: t.description,
      amount: t.amount,
      currency: t.currency,
      transaction_date: t.transaction_date,
      reference: t.reference_id,
      status: t.status,
      type: t.transaction_type
    })),
    ...recentTransactions
  ];
  
  // Sort transactions by date (newest first)
  const sortedTransactions = [...allTransactions].sort((a, b) => 
    new Date(b.transaction_date) - new Date(a.transaction_date)
  );
  
  // Filter transactions based on status
  const filteredTransactions = filterStatus === 'all' 
    ? sortedTransactions.slice(0, 5) 
    : sortedTransactions.filter(t => t.status === filterStatus).slice(0, 5);

  // Format currency based on the currency code
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('zh-HK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date to a more readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('zh-HK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h2 className="text-2xl font-bold mb-4">現金管理儀表板</h2>
          <p className="text-gray-600 mb-4">
            查看您的現金帳戶、最近交易和預定付款，以確保最佳的現金流管理。
          </p>
          <div className="relative h-60 rounded-lg overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1565514020179-026b92b2d0b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
              alt="Cash Management"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-transparent flex items-end p-6">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">有效的現金流管理是業務成功的關鍵</h3>
                <p className="text-sm text-white/90">監控您的現金流，做出明智的財務決策</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {cashFlowKPIs.map((kpi) => (
            <Card key={kpi.kpi_id} className={`shadow-md ${kpi.status === 'excellent' ? 'border-l-4 border-green-500' : kpi.status === 'good' ? 'border-l-4 border-blue-500' : 'border-l-4 border-yellow-500'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">{kpi.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-2xl font-bold">
                      {kpi.unit === '%' ? `${kpi.current_value}%` : 
                       kpi.unit === '天' ? `${kpi.current_value}天` : 
                       kpi.unit === 'HKD' ? formatCurrency(kpi.current_value, 'HKD') : 
                       kpi.current_value}
                    </div>
                    <p className="text-xs text-gray-500">目標: {kpi.unit === '%' ? `${kpi.target_value}%` : 
                       kpi.unit === '天' ? `${kpi.target_value}天` : 
                       kpi.unit === 'HKD' ? formatCurrency(kpi.target_value, 'HKD') : 
                       kpi.target_value}</p>
                  </div>
                  <div className={`p-2 rounded-full ${kpi.trend === 'improving' ? 'bg-green-100' : kpi.trend === 'stable' ? 'bg-blue-100' : 'bg-yellow-100'}`}>
                    {kpi.trend === 'improving' ? <TrendingUp className="h-5 w-5 text-green-600" /> : 
                     kpi.trend === 'stable' ? <Activity className="h-5 w-5 text-blue-600" /> : 
                     <TrendingUp className="h-5 w-5 text-yellow-600" />}
                  </div>
                </div>
                <Progress 
                  value={(kpi.current_value / kpi.target_value) * 100} 
                  className="h-1 mt-2" 
                  indicatorClassName={kpi.status === 'excellent' ? 'bg-green-500' : kpi.status === 'good' ? 'bg-blue-500' : 'bg-yellow-500'}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {cashAccounts.map((account) => (
          <Card key={account.account_id} className="shadow-md">
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle>{account.account_name}</CardTitle>
                <Badge variant={account.status === 'active' ? 'default' : 'outline'}>
                  {account.status === 'active' ? '活躍' : '非活躍'}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">{account.bank_name} • {account.account_number}</p>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-3xl font-bold">{formatCurrency(account.current_balance, account.currency)}</div>
                  <p className="text-sm text-gray-500">可用餘額: {formatCurrency(account.available_balance, account.currency)}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500">
                最後更新: {formatDate(account.last_updated)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-md">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">最近交易</CardTitle>
              <div className="flex space-x-2">
                <select 
                  className="text-xs border rounded p-1"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">全部</option>
                  <option value="completed">已完成</option>
                  <option value="pending">待處理</option>
                </select>
                <button className="p-1 rounded-full hover:bg-gray-100">
                  <Search className="h-4 w-4 text-gray-500" />
                </button>
                <button className="p-1 rounded-full hover:bg-gray-100">
                  <Filter className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
                <div key={transaction.transaction_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full mr-3 ${transaction.amount > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      {transaction.amount > 0 ? 
                        <ArrowUpRight className="h-5 w-5 text-green-600" /> : 
                        <ArrowDownRight className="h-5 w-5 text-red-600" />}
                    </div>
                    <div>
                      <div className="font-medium">{transaction.description}</div>
                      <div className="text-xs text-gray-500">{formatDate(transaction.transaction_date)} • {transaction.reference}</div>
                    </div>
                  </div>
                  <div className={`font-bold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2 text-center text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center">
              <span>查看所有交易</span>
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </button>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-xl">預定付款</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {scheduledPayments.map((payment) => (
                <div key={payment.payment_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full mr-3 bg-blue-100">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">{payment.description}</div>
                      <div className="text-xs text-gray-500">
                        {payment.scheduled_date} • {payment.recipient}
                        {payment.recurring && <span className="ml-2 px-1 py-0.5 bg-blue-100 text-blue-800 rounded">循環</span>}
                      </div>
                    </div>
                  </div>
                  <div className="font-bold">
                    {formatCurrency(payment.amount, payment.currency)}
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2 text-center text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center">
              <span>管理預定付款</span>
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </button>
          </CardContent>
        </Card>
      </div>
      
      {/* Bank Reconciliation Summary */}
      <div className="mt-8">
        <Card className="shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">銀行對帳摘要</CardTitle>
              <button className="p-2 rounded-full hover:bg-gray-100">
                <Download className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">帳戶</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">期間</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">期初餘額</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">期末餘額</th>
                    <th className="text-center py-2 px-3 font-medium text-gray-500">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {financialTransactionsData.bankReconciliations.map((recon) => {
                    const account = cashAccounts.find(a => a.account_id === recon.account_id);
                    return (
                      <tr key={recon.reconciliation_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3">{account?.account_name || recon.account_id}</td>
                        <td className="py-2 px-3">{recon.period}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(recon.starting_balance, 'HKD')}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(recon.ending_balance, 'HKD')}</td>
                        <td className="py-2 px-3 text-center">
                          <Badge variant={recon.reconciliation_status === 'completed' ? 'success' : 'outline'}>
                            {recon.reconciliation_status === 'completed' ? '已完成' : '處理中'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4">
            <p className="text-xs text-gray-500">最後對帳日期: {formatDate(financialTransactionsData.bankReconciliations[0].reconciliation_date)}</p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default CashManagementDashboard;
